import * as React from "react";
import { X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

interface ResponsiveModalProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ResponsiveModal = ({ children, ...props }: ResponsiveModalProps) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <Drawer {...props}>{children}</Drawer>;
  }

  return <Dialog {...props}>{children}</Dialog>;
};

const ResponsiveModalTrigger = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogTrigger>) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <DrawerTrigger className={className} {...props}>
        {children}
      </DrawerTrigger>
    );
  }

  return (
    <DialogTrigger className={className} {...props}>
      {children}
    </DialogTrigger>
  );
};

const ResponsiveModalClose = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogClose>) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <DrawerClose className={className} {...props}>
        {children}
      </DrawerClose>
    );
  }

  return (
    <DialogClose className={className} {...props}>
      {children}
    </DialogClose>
  );
};

const ResponsiveModalContent = ({
  className,
  children,
  fullscreen,
  hideClose,
  ...props
}: React.ComponentProps<typeof DialogContent> & { fullscreen?: boolean; hideClose?: boolean }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <DrawerContent
        hideHandle={fullscreen}
        className={cn(
          fullscreen && "h-dvh max-h-dvh rounded-none",
          className,
        )}
        {...props}
      >
        {children}
        {fullscreen && !hideClose && (
          <DrawerClose className="absolute right-4 bottom-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-foreground/10 text-foreground/60 shadow-lg transition-colors hover:bg-foreground/20">
            <X className="h-5 w-5" />
            <span className="sr-only">Fechar</span>
          </DrawerClose>
        )}
      </DrawerContent>
    );
  }

  return (
    <DialogContent className={cn(className)} {...props}>
      {children}
    </DialogContent>
  );
};

const ResponsiveModalHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <DrawerHeader className={cn(className)} {...props} />;
  }

  return <DialogHeader className={cn(className)} {...props} />;
};

const ResponsiveModalFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <DrawerFooter className={cn(className)} {...props} />;
  }

  return <DialogFooter className={cn(className)} {...props} />;
};

const ResponsiveModalTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogTitle>) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <DrawerTitle className={cn(className)} {...props} />;
  }

  return <DialogTitle className={cn(className)} {...props} />;
};

const ResponsiveModalDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof DialogDescription>) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <DrawerDescription className={cn(className)} {...props} />;
  }

  return <DialogDescription className={cn(className)} {...props} />;
};

export {
  ResponsiveModal,
  ResponsiveModalTrigger,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
};
